import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ShellSkeleton } from '@/components/ui/ShellSkeleton'
import { api, clearSession, getRole, getToken, restoreSession, setSession } from '@/lib/api'
import RBAC from '@/lib/rbac.generated.json'

/** Mirror of the backend RBAC (src/core/security/rbac.py) for UI gating only —
 *  the server always re-checks; hiding a nav item is a convenience, not security. */
export type Role = 'owner' | 'admin' | 'finance' | 'support' | 'analyst'

interface LoginResponse {
  access_token: string
  role: string
}

/** POST /auth/miniapp */
interface MiniAppResponse {
  access_token?: string | null
  role?: string | null
  totp_required?: boolean
  challenge?: string | null
}

/** GET /auth/me (src/api/admin/me.py) */
export interface MeResponse {
  id: number
  email: string
  role: string
  permissions: string[]
}

interface AuthState {
  token: string | null
  role: Role | null
  /** the email this browser signed in with (shown in the rail footer); null after a Telegram
   *  sign-in or when the browser does not remember it */
  email: string | null
  /** the role's permissions as the server answered GET /auth/me; null until it answers */
  permissions: readonly string[] | null
  login: (email: string, password: string, totp: string) => Promise<void>
  /** Mini App sign-in, step 1: the signed initData → a TOTP challenge (src/api/admin/auth.py
   *  `/auth/miniapp`; the server always asks for the code on this path). */
  startTelegram: (initData: string) => Promise<string>
  /** step 2: the challenge + the 6-digit code → the session (email stays null). */
  verifyTelegram: (challenge: string, totp: string) => Promise<void>
  logout: () => void
  /** «Выйти на всех устройствах»: the server bumps this admin's token version — every session
   *  (this one too) ends; then the local state is cleared like `logout`. */
  logoutEverywhere: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

// The operator's own email for the rail footer (WP-16). Not a credential: the session lives in
// memory and the httpOnly cookie (F-22); the email only names who is signed in on this browser,
// and goes away on «Выйти».
const EMAIL_KEY = 'nh_admin_email'

function readEmail(): string | null {
  try {
    return window.localStorage.getItem(EMAIL_KEY)
  } catch {
    return null
  }
}

function writeEmail(email: string | null): void {
  try {
    if (email === null) window.localStorage.removeItem(EMAIL_KEY)
    else window.localStorage.setItem(EMAIL_KEY, email)
  } catch {
    // storage blocked: the footer shows the role only
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // SECURITY (F-22): tokens are in memory only; a reload restores the session from the
  // httpOnly refresh cookie before the routes render (no flash of the login screen).
  const [token, setToken] = useState<string | null>(getToken())
  const [role, setRole] = useState<Role | null>(getRole() as Role | null)
  const [ready, setReady] = useState<boolean>(getToken() !== null)
  const [email, setEmail] = useState<string | null>(readEmail)
  const [permissions, setPermissions] = useState<readonly string[] | null>(null)

  // The permission list of the signed-in role (the single source for every button's gate).
  // A failure keeps the generated copy of rbac.py: the server re-checks every call anyway.
  useEffect(() => {
    if (token === null || role === null) {
      setLivePermissions(null, null)
      setPermissions(null)
      return
    }
    let alive = true
    void api
      .get<MeResponse>('/auth/me')
      .then((me) => {
        if (!alive || !Array.isArray(me.permissions)) return
        const meRole = (me.role as Role) ?? role
        setLivePermissions(meRole, me.permissions)
        setPermissions(me.permissions)
        if (meRole !== role) setRole(meRole)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [token, role])

  useEffect(() => {
    if (ready) return
    let alive = true
    void restoreSession().then((s) => {
      if (!alive) return
      if (s !== null) {
        setToken(s.access_token)
        setRole(s.role as Role)
      }
      setReady(true)
    })
    return () => {
      alive = false
    }
  }, [ready])

  const logout = useCallback(() => {
    // Server-side revocation of the refresh cookie (and its family), then clear locally.
    void api.post('/auth/logout').catch(() => {})
    clearSession()
    writeEmail(null)
    setEmail(null)
    setToken(null)
    setRole(null)
  }, [])

  const logoutEverywhere = useCallback(async () => {
    await api.post('/auth/sessions/revoke-all')
    clearSession()
    writeEmail(null)
    setEmail(null)
    setToken(null)
    setRole(null)
  }, [])

  useEffect(() => {
    window.addEventListener('nh:unauthorized', logout)
    return () => window.removeEventListener('nh:unauthorized', logout)
  }, [logout])

  const login = useCallback(async (email: string, password: string, totp: string) => {
    const res = await api.post<LoginResponse>('/auth/login', {
      email,
      password,
      totp_code: totp,
    })
    setSession(res.access_token, res.role)
    writeEmail(email)
    setEmail(email)
    setToken(res.access_token)
    setRole(res.role as Role)
  }, [])

  const startTelegram = useCallback(async (initData: string) => {
    const res = await api.post<MiniAppResponse>('/auth/miniapp', { init_data: initData })
    if (res.access_token && res.role) {
      // an older server without the mandatory code: take the session as it is
      setSession(res.access_token, res.role)
      setToken(res.access_token)
      setRole(res.role as Role)
      return ''
    }
    if (!res.challenge) throw new Error('no challenge')
    return res.challenge
  }, [])

  const verifyTelegram = useCallback(async (challenge: string, totp: string) => {
    const res = await api.post<LoginResponse>('/auth/miniapp/verify', { challenge, totp_code: totp })
    setSession(res.access_token, res.role)
    writeEmail(null)
    setEmail(null)
    setToken(res.access_token)
    setRole(res.role as Role)
  }, [])

  const value = useMemo<AuthState>(
    () => ({ token, role, email, permissions, login, startTelegram, verifyTelegram, logout, logoutEverywhere }),
    [token, role, email, permissions, login, startTelegram, verifyTelegram, logout, logoutEverywhere],
  )
  // Restoring the session: the shape of the shell, not a word on an empty page (WP-08).
  if (!ready) return <ShellSkeleton />
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (ctx === null) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

const PERMS: Record<Role, ReadonlySet<string>> = {
  owner: new Set(['*']),
  admin: new Set([
    'dashboard',
    'jobs',
    'users',
    'activity',
    'pricing',
    'finance',
    'payouts',
    'promo',
    'broadcasts',
    'support',
    'audit',
    'content',
    'contacts', // «Контакты и ссылки» (contacts.edit)
    'accounting',
    'acquisition',
    'growth',
    'webhooks',
  ]),
  finance: new Set([
    'dashboard',
    'jobs',
    'users',
    'activity',
    'pricing',
    'finance',
    'payouts',
    'promo',
    'accounting',
    'acquisition',
    'growth',
    'webhooks', // payments tab only (webhooks.view); generation needs webhooks.generation
  ]),
  // growth = gifts + Stars auto-renew list (payments.view); cancelling needs payments.refund
  support: new Set(['dashboard', 'jobs', 'users', 'activity', 'support', 'growth']),
  // acquisition = «Привлечение» reports (acquisition.view); support has no access: the reports
  // carry revenue and ad spend (docs/acquisition.md §7)
  analyst: new Set(['dashboard', 'jobs', 'users', 'activity', 'finance', 'accounting', 'acquisition', 'growth']),
}

export function canAccess(role: Role | null, section: string): boolean {
  if (role === null) return false
  const perms = PERMS[role]
  return perms.has('*') || perms.has(section)
}

/**
 * Fine-grained actions — the server's permission list is the one source (audit 2026-10-22
 * §7.4). After sign-in the app asks GET /auth/me and gates by exactly what it answers. Before
 * that answer (the first paint), and for a role other than the signed-in one (stories, tests),
 * the generated copy of rbac.py is used: `rbac.generated.json`, checked against the server by
 * tests/integration/test_admin_pack_f.py — never edited by hand.
 */
const ROLE_PERMISSIONS = RBAC as Record<string, readonly string[]>

let live: { role: Role; perms: ReadonlySet<string> } | null = null

/** The signed-in operator's permissions from /auth/me (null = not known yet / signed out). */
export function setLivePermissions(role: Role | null, perms: readonly string[] | null): void {
  live = role !== null && perms !== null ? { role, perms: new Set(perms) } : null
}

export function permissionsOf(role: Role | null): ReadonlySet<string> {
  if (role === null) return new Set()
  if (live !== null && live.role === role) return live.perms
  return new Set(ROLE_PERMISSIONS[role] ?? [])
}

export function canDo(role: Role | null, action: string): boolean {
  return permissionsOf(role).has(action)
}
